import type { Camera } from '@babylonjs/core/Cameras/camera';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { Scene } from '@babylonjs/core/scene';
import { eyeAxes } from './overhead';

/** Where in the canvas a label is drawn, in pixels. */
export interface SlotRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FloatingLabelOptions<T> {
  name: string;
  parent: TransformNode | null;
  /** Canvas size, and the size of one label within it. The two ratios should match. */
  atlas: { width: number; height: number };
  slot: { width: number; height: number };
  /** How big a label is in the world, in metres. */
  size: { width: number; height: number };
  /** Paints one label into its slot. The canvas is cleared before the batch. */
  draw: (ctx: CanvasRenderingContext2D, rect: SlotRect, item: T) => void;
  /** What of an item its label shows, to tell when a set needs painting: `String(item)`. */
  key?: (item: T) => string;
  /**
   * Labels that grow with the eye's distance: their size at `reference` metres and nearer, up to
   * `max` times it further off. They grow about the point they stand over, lift and all, so they
   * keep clear of the piece under them. None: one size at any distance.
   */
  grow?: { reference: number; max: number };
}

export interface FloatingLabels<T> {
  /**
   * Replaces every label; more than the canvas holds are dropped. Each stands over a point in
   * the world (however the sheet is parented), its centre `lift` metres above it along the eye's
   * up (none: centred on the point).
   */
  set(items: readonly { at: Vector3; item: T; lift?: number }[]): void;
  /** Turns every label towards the eye; call each frame. */
  face(camera: Camera): void;
  /** Moves the points the labels stand over, without repainting them: signs follow a walker. */
  moveTo(points: readonly Vector3[]): void;
  /** Paints the labels at the next set even if they say the same: their look changed (a palette). */
  invalidate(): void;
  readonly count: number;
  dispose(): void;
}

/**
 * A sheet of labels that always face the viewer.
 *
 * Every label is painted into one canvas and shown on its own quad of a single mesh, whose quads
 * are turned towards the eye each frame. Thin instances cannot billboard one by one and a mesh per
 * label would be a draw call each, so this is what keeps a boardful of them inside the Quest's
 * budget: one draw call however many there are.
 *
 * The caller says what a label looks like, so the odds over an attack target and the sign over a
 * frozen piece are the same machinery painted differently.
 */
export function createFloatingLabels<T>(
  scene: Scene,
  options: FloatingLabelOptions<T>,
): FloatingLabels<T> {
  const { atlas: size, slot, size: world } = options;
  const perRow = Math.floor(size.width / slot.width);
  const capacity = perRow * Math.floor(size.height / slot.height);

  const texture = new DynamicTexture(`${options.name}Atlas`, size, scene, true);
  texture.hasAlpha = true;
  // The shader adds the emissive texture to the emissive colour: black leaves the canvas as it is.
  const material = new StandardMaterial(`${options.name}Material`, scene);
  material.disableLighting = true;
  material.emissiveColor = Color3.Black();
  material.emissiveTexture = texture;
  material.opacityTexture = texture;
  material.backFaceCulling = false;

  const mesh = new Mesh(options.name, scene);
  mesh.material = material;
  mesh.isPickable = false;
  mesh.parent = options.parent;
  // The quads move every frame, so their bounds are never right: skip culling instead.
  mesh.alwaysSelectAsActiveMesh = true;
  mesh.isVisible = false;

  /** The points the labels stand over, in the sheet's own space, and each one's lift. */
  let centres: Vector3[] = [];
  let lifts: number[] = [];
  let positions = new Float32Array(0);
  const keyOf = options.key ?? ((item: T) => String(item));
  /**
   * What the canvas shows, and how many quads the mesh has. A set that changes neither paints
   * and builds nothing: every session update (a clock tick online, a selection, a hold) set the
   * labels again, and each cleared, redrew and uploaded the whole canvas and rebuilt the mesh
   * (client-board:bugs#6).
   */
  let painted: string | null = null;
  let built = 0;
  let lastCamera: Camera | null = null;
  const inverse = new Matrix();
  const eye = new Vector3();
  const forward = new Vector3();
  const right = new Vector3();
  const up = new Vector3();
  const lifted = new Vector3();

  /**
   * A label is placed in the world (a piece's absolute position) but drawn in the sheet's own
   * space, under a board that is turned to face the player and stands on its table: taken as
   * local, a world position lands point-mirrored through the board's centre, which is how a
   * frozen Djinni's sign came to hang over an empty square on the far side.
   */
  const toLocal = (world: Vector3, out: Vector3): Vector3 => {
    Vector3.TransformCoordinatesToRef(world, inverse, out);
    return out;
  };
  const refreshInverse = () => mesh.computeWorldMatrix(true).invertToRef(inverse);

  return {
    get count() {
      return centres.length;
    },

    set(items) {
      const shown = items.slice(0, capacity);
      if (shown.length === 0) {
        centres = [];
        lifts = [];
        mesh.isVisible = false;
        return;
      }
      refreshInverse();
      const key = shown.map(({ item }) => keyOf(item)).join('\u0000');
      if (key !== painted) {
        const ctx = texture.getContext() as CanvasRenderingContext2D;
        ctx.clearRect(0, 0, size.width, size.height);
        shown.forEach(({ item }, i) =>
          options.draw(
            ctx,
            {
              x: (i % perRow) * slot.width,
              y: Math.floor(i / perRow) * slot.height,
              w: slot.width,
              h: slot.height,
            },
            item,
          ),
        );
        texture.update(true);
        painted = key;
      }

      centres = shown.map(({ at }) => toLocal(at, new Vector3()));
      lifts = shown.map(({ lift }) => lift ?? 0);
      mesh.isVisible = true;
      if (shown.length === built) {
        // The same quads over the new points, at once rather than at the next frame.
        if (lastCamera) face(lastCamera);
        return;
      }
      built = shown.length;
      positions = new Float32Array(shown.length * 12);
      const uvs = new Float32Array(shown.length * 8);
      const normals = new Float32Array(shown.length * 12);
      const indices = new Uint32Array(shown.length * 6);
      shown.forEach((_, i) => {
        // Slot i of the canvas; the texture is flipped, so the canvas top is v = 1.
        const u0 = ((i % perRow) * slot.width) / size.width;
        const u1 = u0 + slot.width / size.width;
        const v1 = 1 - (Math.floor(i / perRow) * slot.height) / size.height;
        const v0 = v1 - slot.height / size.height;
        uvs.set([u0, v0, u1, v0, u1, v1, u0, v1], i * 8);
        for (let k = 0; k < 4; k++) normals.set([0, 1, 0], i * 12 + k * 3);
        indices.set(
          [0, 1, 2, 0, 2, 3].map((n) => n + i * 4),
          i * 6,
        );
      });
      const data = new VertexData();
      data.positions = positions;
      data.uvs = uvs;
      data.normals = normals;
      data.indices = indices;
      data.applyToMesh(mesh, true);
      if (lastCamera) face(lastCamera);
    },

    moveTo(next) {
      if (centres.length === 0) return;
      refreshInverse();
      for (let i = 0; i < centres.length && i < next.length; i++) toLocal(next[i]!, centres[i]!);
    },

    face,

    invalidate() {
      painted = null;
    },

    dispose() {
      mesh.dispose();
      material.dispose();
      texture.dispose();
    },
  };

  function face(camera: Camera): void {
    lastCamera = camera;
    if (centres.length === 0) return;
    refreshInverse();
    Vector3.TransformCoordinatesToRef(camera.globalPosition, inverse, eye);
    const grow = options.grow;
    centres.forEach((point, i) => {
      const g = grow
        ? Math.min(grow.max, Math.max(1, Vector3.Distance(point, eye) / grow.reference))
        : 1;
      const hw = (world.width / 2) * g;
      const hh = (world.height / 2) * g;
      eyeAxes(point, eye, right, up, forward);
      const c = point.addToRef(up.scaleToRef(lifts[i]! * g, lifted), lifted);
      const o = i * 12;
      // Bottom-left, bottom-right, top-right, top-left, as seen from the eye.
      positions[o] = c.x - right.x * hw - up.x * hh;
      positions[o + 1] = c.y - right.y * hw - up.y * hh;
      positions[o + 2] = c.z - right.z * hw - up.z * hh;
      positions[o + 3] = c.x + right.x * hw - up.x * hh;
      positions[o + 4] = c.y + right.y * hw - up.y * hh;
      positions[o + 5] = c.z + right.z * hw - up.z * hh;
      positions[o + 6] = c.x + right.x * hw + up.x * hh;
      positions[o + 7] = c.y + right.y * hw + up.y * hh;
      positions[o + 8] = c.z + right.z * hw + up.z * hh;
      positions[o + 9] = c.x - right.x * hw + up.x * hh;
      positions[o + 10] = c.y - right.y * hw + up.y * hh;
      positions[o + 11] = c.z - right.z * hw + up.z * hh;
    });
    mesh.updateVerticesData('position', positions, false, false);
  }
}
